import Head_nurse_management from "@/pages/roster-management/Head_nurse_management";
import { useGroupSelection } from "@hooks/useGroupSelection.ts";
import useNurses from "@hooks/useNurses.ts";
import ErrorComponent from "@components/ui/error.tsx";
import useGradeConfig from "@hooks/useGradeConfig.ts";
import { useShiftsQuery } from "@hooks/roster-craete/useShiftsQuery.ts";
import { useIsAdmin } from "@hooks/useAuth.ts";
import { useTeamsQuery } from "@/hooks/roster-craete/useTeamsQuery";
import RosterManagementSkeleton from "./components/RosterManagementSkeleton";
import ContainerLoadingOverlay from "@/components/modal/ContainerLoadingOverlay";
import { useEffect, useMemo, useRef, useState } from "react";
import useGroupMembers from "@/hooks/useGroupMembers";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useNurseAsOfMonthStore } from "@/store/nurseAsOfMonthStore";
import { usePrecepteePeriodsQuery } from "@/hooks/roster-craete/usePrecepteePeriodsQuery";

export default function RosterManagement() {
  const { selectedGroupId, isGroupSelectionLoading } = useGroupSelection();
  const isAdmin = useIsAdmin();
  const now = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState<number>(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(
    now.getMonth() + 1
  );
  // 선택월을 store 에 동기화 → 저장(bulk-update)이 그 달 valid_from 으로 발효되게.
  useEffect(() => {
    useNurseAsOfMonthStore.getState().setAsOfMonth(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);
  // 이 화면을 벗어나면 store 리셋 → 타 화면 저장이 stale 월을 쓰지 않게(백엔드 today 폴백).
  useEffect(
    () => () => useNurseAsOfMonthStore.getState().setAsOfMonth(null, null),
    []
  );
  const hasRenderedContentRef = useRef(false);
  const renderedScopeRef = useRef<string | null>(null);

  // 필수 요청들의 로딩/에러 상태를 하나로 묶어 화면 전환 상태를 제어한다.
  const nursesResult = useNurses(selectedGroupId, {
    year: selectedYear,
    month: selectedMonth,
  });
  const teamsResult = useTeamsQuery(isAdmin ? (selectedGroupId ?? "") : null);
  const configResult = useGradeConfig({
    selectedGroupId: isAdmin ? (selectedGroupId ?? "") : null,
  });
  const rosterConfigResult = useRosterConfig();
  const precepteePeriodsResult = usePrecepteePeriodsQuery({
    groupId: selectedGroupId,
    year: selectedYear,
    month: selectedMonth,
  });
  const shiftsResult = useShiftsQuery(isAdmin ? (selectedGroupId ?? "") : "");
  const monthMembersResult = useGroupMembers(
    selectedGroupId,
    selectedYear,
    selectedMonth
  );
  const shouldWaitMonthMembers = !!selectedGroupId;

  const isLoading = [
    isGroupSelectionLoading,
    nursesResult.isLoading,
    teamsResult.isLoading,
    configResult.isLoading,
    rosterConfigResult.isLoading,
    precepteePeriodsResult.isLoading,
    shiftsResult.isLoading,
    shouldWaitMonthMembers && monthMembersResult.isLoading,
  ].some(Boolean);

  const isFetching = [
    isGroupSelectionLoading,
    nursesResult.isFetching,
    teamsResult.isFetching,
    configResult.isFetching,
    rosterConfigResult.isFetching,
    precepteePeriodsResult.isFetching,
    shiftsResult.isFetching,
    shouldWaitMonthMembers && monthMembersResult.isFetching,
  ].some(Boolean);

  const isError = [
    nursesResult.isError,
    teamsResult.isError,
    configResult.isError,
    rosterConfigResult.isError,
    precepteePeriodsResult.isError,
    shiftsResult.isError,
    monthMembersResult.isError,
  ].some(Boolean);

  const nurseError = nursesResult.error;
  const teamsErrorData = teamsResult.error;
  const configError = configResult.error;
  const rosterConfigError = rosterConfigResult.error;
  const precepteePeriodsError = precepteePeriodsResult.error;
  const shiftsError = shiftsResult.error;
  const monthMembersError = monthMembersResult.error;
  const loadingOverlayTitle = `${selectedYear}년 ${selectedMonth}월 근무자 데이터를 불러오는 중입니다`;
  const currentScopeKey = `${selectedGroupId ?? ""}:${selectedYear}-${selectedMonth}`;
  const isScopeChanging =
    hasRenderedContentRef.current && renderedScopeRef.current !== currentScopeKey;
  const shouldShowTransitionOverlay =
    hasRenderedContentRef.current && isScopeChanging && isFetching;

  const hasRequiredData =
    !isGroupSelectionLoading &&
    !!nursesResult.data &&
    !!teamsResult.data &&
    !!configResult.data &&
    !!rosterConfigResult.data &&
    !!precepteePeriodsResult.data &&
    !!shiftsResult.data &&
    (!shouldWaitMonthMembers || !!monthMembersResult.data);

  const shiftMonth = (delta: number) => {
    const total = selectedYear * 12 + (selectedMonth - 1) + delta;
    setSelectedYear(Math.floor(total / 12));
    setSelectedMonth((total % 12) + 1);
  };

  useEffect(() => {
    if (hasRequiredData && !isLoading) {
      hasRenderedContentRef.current = true;
      renderedScopeRef.current = currentScopeKey;
    }
  }, [currentScopeKey, hasRequiredData, isLoading]);

  if (isError) {
    return (
      <ErrorComponent
        errorMsg={
          nurseError?.message ||
          shiftsError?.message ||
          precepteePeriodsError?.message ||
          teamsErrorData?.message ||
          (configError as Error)?.message ||
          (rosterConfigError as Error)?.message ||
          monthMembersError?.message ||
          "데이터를 불러오는 중 오류가 발생했습니다."
        }
      />
    );
  }

  if (!hasRenderedContentRef.current && !hasRequiredData && isLoading) {
    return (
      <div className="pb-20 mgmt_inner">
        <RosterManagementSkeleton />
      </div>
    );
  }

  return (
    <div className="pb-20 mgmt_inner relative">
      <ContainerLoadingOverlay
        isOpen={shouldShowTransitionOverlay}
        title={loadingOverlayTitle}
      />
      <div className="w-full min-w-[1200px] flex items-start justify-start">
        <Head_nurse_management
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          nurseDatas={nursesResult.data}
          precepteePeriods={precepteePeriodsResult.data?.items}
          teamsSchema={teamsResult.data}
          gradeData={configResult.data}
          shiftsData={shiftsResult.data}
          rosterConfig={rosterConfigResult.data}
          monthMembers={monthMembersResult.data}
          isMonthMembersFetching={monthMembersResult.isFetching}
          onShiftMonth={shiftMonth}
        />
      </div>
    </div>
  );
}
